import { isJaneUser } from '@iheartjane/data-access';
import { useToast } from '@iheartjane/reefer';
import { useCallback } from 'react';

import { FavoriteSavedToastLabel } from '@jane/bloom/components/toast/favoriteSavedToastLabel';
import { useFetcherWithMutation } from '@jane/bloom/hooks/useFetcherWithMutation';
import {
  getPendingFavorite,
  setPendingFavorite,
  usePendingFavorite,
} from '@jane/bloom/hooks/usePendingFavorite';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { useAuthModal } from '@jane/bloom/providers/authModalProvider';

interface FavoriteVariables {
  isFavorite: boolean;
  productId: number;
}

export const useFavoriteToggle = (
  productId: number,
  savedIsFavorite = false
) => {
  const { whoami } = useRootLoaderData();
  const isSignedIn = isJaneUser(whoami.user);
  const { openAuthModal } = useAuthModal();
  const toast = useToast();
  const pendingFavorite = usePendingFavorite(productId);
  const isFavorite = isSignedIn && (pendingFavorite ?? savedIsFavorite);

  const { isPending, mutate } = useFetcherWithMutation<FavoriteVariables, null>(
    {
      action: '/api/product-favorites',
      defaultShouldRevalidate: false,
      key: `product-favorite-${productId}`,
    }
  );

  const saveFavorite = useCallback(
    (nextIsFavorite: boolean) => {
      const save = () => {
        const previousPending = getPendingFavorite(productId);
        setPendingFavorite(productId, nextIsFavorite);
        mutate(
          { isFavorite: nextIsFavorite, productId },
          {
            onError: (errors) => {
              setPendingFavorite(productId, previousPending);

              if (
                (errors as { reason?: string } | null)?.reason ===
                'unauthenticated'
              ) {
                openAuthModal({ onSuccess: save });
                return;
              }

              toast.add({
                label: 'Something went wrong, please try again',
                variant: 'error',
              });
            },
          }
        );
      };

      save();
    },
    [mutate, openAuthModal, productId, toast]
  );

  const toggleFavorite = useCallback(() => {
    if (!isSignedIn) {
      openAuthModal({
        onSuccess: () => saveFavorite(true),
      });
      return;
    }

    if (isPending) return;

    if (!isFavorite) {
      toast.add({ label: <FavoriteSavedToastLabel /> });
    }
    saveFavorite(!isFavorite);
  }, [isFavorite, isPending, isSignedIn, openAuthModal, saveFavorite, toast]);

  return { isFavorite, toggleFavorite };
};
